import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { Users, X, Loader2, Trash2 } from 'lucide-react';
import { useProject, useProjectMembers, useInviteMember, useRemoveMember } from '@/hooks/use-projects';
import { InviteMemberSchema, type InvitableRole } from '@/types/project';
import { toast } from '@/hooks/use-toast';

interface ProjectMembersDialogProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
}

const ROLE_TONE: Record<string, BadgeTone> = {
  owner: 'warning',
  editor: 'info',
  viewer: 'neutral',
};

export function ProjectMembersDialog({ projectId, isOpen, onClose }: ProjectMembersDialogProps) {
  const { data: project } = useProject(projectId);
  const { data: members = [], isLoading } = useProjectMembers(projectId);
  const invite = useInviteMember(projectId);
  const remove = useRemoveMember(projectId);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InvitableRole>('editor');

  // Only the owner can invite/remove; everyone else sees a read-only roster.
  const canManage = project?.callerRole === 'owner';

  if (!isOpen) return null;

  const handleInvite = async () => {
    const parsed = InviteMemberSchema.safeParse({ email, role });
    if (!parsed.success) {
      toast({ title: 'Invalid', description: parsed.error.issues[0]?.message ?? 'Check the email.', variant: 'destructive' });
      return;
    }
    try {
      await invite.mutateAsync(parsed.data);
      toast({ title: 'Invited', description: `${parsed.data.email} can now access this project.` });
      setEmail('');
    } catch (err) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed to invite.', variant: 'destructive' });
    }
  };

  const handleRemove = async (memberEmail: string) => {
    try {
      await remove.mutateAsync(memberEmail);
    } catch (err) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed to remove.', variant: 'destructive' });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background border rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-semibold flex items-center gap-2">
            <Users className="h-4 w-4" />
            Share project
          </h2>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          {canManage ? (
            <>
              <p className="text-sm text-muted-foreground">
                Invite teammates by email. They get access the moment they sign in with that
                Google account — editors can edit, viewers can only view.
              </p>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                  placeholder="teammate@company.com"
                  className="flex-1 px-3 py-2 text-sm border rounded-md bg-background focus:outline-hidden focus:ring-2 focus:ring-ring"
                />
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value as InvitableRole)}
                  className="px-2 py-2 text-sm border rounded-md bg-background focus:outline-hidden focus:ring-2 focus:ring-ring"
                >
                  <option value="editor">Editor</option>
                  <option value="viewer">Viewer</option>
                </select>
                <Button size="sm" onClick={handleInvite} disabled={invite.isPending}>
                  {invite.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Invite'}
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              People with access to this project. Only the owner can change who's a member.
            </p>
          )}

          <div className="space-y-2">
            {isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading members…
              </div>
            ) : (
              members.map((m) => (
                <div key={m.email} className="flex items-center gap-2 p-2 border rounded text-sm">
                  <span className="flex-1 truncate">{m.email}</span>
                  <Badge tone={ROLE_TONE[m.role] ?? 'neutral'}>{m.role}</Badge>
                  {canManage && m.role !== 'owner' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemove(m.email)}
                      disabled={remove.isPending}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
